import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/services/supabase";
import { SUPABASE_PUBLISHABLE_KEY, SUPABASE_URL } from "@/config/publicConfig";

/**
 * 众筹兑换码后台：建批次 / 下载 CSV / 批次统计 / 查码 / 作废 / 补发 / 直接发放。
 * 鉴权由服务端 admin-redemption-codes 强制（is_admin），前端的 sessionStorage 标记只是体验层。
 * 明文码只在「建批次」和「补发」的响应里出现一次，关掉页面就再也取不回来。
 */

// ---- 档位预设（金额为建议值，见 众筹与兑换码实施计划 4.3）----
const PRESETS = [
  { tier: "A", label: "A 純粋応援 ¥500", name: "純粋応援", days: 7, badge: "supporter" },
  { tier: "B", label: "B お試しサポーター ¥1,500", name: "お試しサポーター", days: 45, badge: "supporter" },
  { tier: "C", label: "C 半年サポーター ¥3,000", name: "半年サポーター", days: 180, badge: "supporter" },
  { tier: "D", label: "D 【早割】1年サポーター ¥4,800", name: "【早割】1年サポーター", days: 365, badge: "supporter" },
  { tier: "E", label: "E 1年サポーター ¥6,000", name: "1年サポーター", days: 365, badge: "supporter" },
  { tier: "F", label: "F 親子・ギフトペア ¥10,000（每位支持者 2 个码）", name: "親子・ギフトペア", days: 365, badge: "supporter" },
  { tier: "G", label: "G 創設サポーター ¥20,000", name: "創設サポーター", days: 730, badge: "founder" },
  { tier: "H", label: "H パトロン ¥50,000", name: "パトロン", days: 1095, badge: "patron" },
] as const;

const ERROR_TEXT: Record<string, string> = {
  FORBIDDEN: "没有管理员权限",
  UNAUTHORIZED: "登录已失效，请重新登录后台",
  MISSING_FIELDS: "请填写完整（活动名、档位、名称）",
  INVALID_COUNT: "数量必须是 1~2000 的整数",
  INVALID_DAYS: "天数必须是 1~1095 的整数",
  INVALID_BADGE: "徽章不合法",
  INVALID_DEADLINE: "截止日期不合法",
  DEADLINE_TOO_LATE: "兑换截止日不能晚于发码日起 180 天（资金结算法 6 个月规则）",
  DEADLINE_IN_PAST: "兑换截止日必须晚于现在",
  CODE_NOT_FOUND: "没有找到这个码",
  HINT_NOT_UNIQUE: "末 4 位对应多个码，请输入完整码",
  INVALID_FORMAT: "码的格式不正确",
  CODE_ALREADY_REDEEMED: "这个码已经被兑换，无法作废或补发",
  BATCH_NOT_ACTIVE: "所属批次不是「有效」状态，无法补发",
  BATCH_EXPIRED: "所属批次已过兑换截止日，无法补发（补发不能延长有效期）",
  USER_NOT_FOUND: "没有找到这个会员 ID",
  INVALID_USER_ID: "会员 ID 格式不正确",
  REASON_REQUIRED: "请填写理由",
  SERVER_MISCONFIGURED: "服务端未配置 REDEMPTION_CODE_PEPPER",
};

async function callAdmin<T = any>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error(ERROR_TEXT.UNAUTHORIZED);
  const res = await fetch(`${SUPABASE_URL}/functions/v1/admin-redemption-codes`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: SUPABASE_PUBLISHABLE_KEY, Authorization: `Bearer ${token}` },
    body: JSON.stringify({ action, ...params }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || body.error) throw new Error(ERROR_TEXT[body.error] ?? body.detail ?? body.error ?? "请求失败");
  return body as T;
}

/** 兑换链接跟随当前站点：后台连的是哪个环境，链接就指向哪个环境的 /redeem */
function redeemUrlFor(code: string): string {
  return `${window.location.origin}/redeem#code=${code}`;
}

function csvEscape(v: unknown): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename: string, rows: string[][]) {
  // 前置 BOM，Excel / Numbers 打开日文不乱码
  const blob = new Blob(["﻿" + rows.map((r) => r.map(csvEscape).join(",")).join("\r\n")], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function dateInputValue(daysFromNow: number): string {
  const d = new Date(Date.now() + daysFromNow * 86400_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

interface BatchRow {
  id: string;
  campaign: string;
  reward_tier: string;
  name: string;
  vip_days: number;
  badge: string | null;
  redeem_deadline: string;
  status: "active" | "paused" | "revoked";
  code_count: number;
  unused: number;
  redeemed: number;
  revoked: number;
  expired: boolean;
}

interface CreatedResult {
  batch_id: string;
  codes: { code: string; hint: string; redeem_url: string }[];
  meta: { campaign: string; reward_tier: string; name: string; vip_days: number; badge: string | null; redeem_deadline: string };
}

const inputCls = "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500";
const btnCls = "px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50";
const btnGhost = "px-3 py-1 rounded border text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50";

export function RedemptionCodesPage() {
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [loading, setLoading] = useState(false);

  const flash = (msg: string) => {
    setInfo(msg);
    setError("");
  };
  const fail = (e: unknown) => {
    setError(e instanceof Error ? e.message : String(e));
    setInfo("");
  };

  const loadBatches = useCallback(async () => {
    try {
      const r = await callAdmin<{ batches: BatchRow[] }>("list_batches");
      setBatches(r.batches);
    } catch (e) {
      fail(e);
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // ---------- 建批次 ----------
  const [presetIdx, setPresetIdx] = useState(4);
  const [campaign, setCampaign] = useState("campfire-2026");
  const [name, setName] = useState<string>(PRESETS[4].name);
  const [days, setDays] = useState<number>(PRESETS[4].days);
  const [badge, setBadge] = useState<string>(PRESETS[4].badge);
  const [count, setCount] = useState(10);
  const [deadline, setDeadline] = useState(dateInputValue(170));
  const [note, setNote] = useState("");
  const [created, setCreated] = useState<CreatedResult | null>(null);

  const applyPreset = (i: number) => {
    setPresetIdx(i);
    setName(PRESETS[i].name);
    setDays(PRESETS[i].days);
    setBadge(PRESETS[i].badge);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!window.confirm(`确认生成 ${count} 个「${name}」（${days} 天）的兑换码？\n明文码只会显示这一次，请立刻下载 CSV。`)) return;
    setLoading(true);
    try {
      // 截止日填当天 23:59:59（本地时区）
      const deadlineIso = new Date(`${deadline}T23:59:59`).toISOString();
      const r = await callAdmin<CreatedResult>("create_batch", {
        campaign,
        reward_tier: PRESETS[presetIdx].tier,
        name,
        vip_days: days,
        badge,
        count,
        redeem_deadline: deadlineIso,
        note: note || null,
      });
      setCreated(r);
      flash(`已生成 ${r.codes.length} 个码。请立即下载 CSV——离开本页后无法再次取回明文。`);
      loadBatches();
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!created) return;
    const m = created.meta;
    downloadCsv(
      `codes-${m.campaign}-${m.reward_tier}-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        ["code", "redeem_url", "hint", "reward_tier", "name", "vip_days", "redeem_deadline"],
        ...created.codes.map((c) => [c.code, redeemUrlFor(c.code), c.hint, m.reward_tier, m.name, String(m.vip_days), m.redeem_deadline]),
      ],
    );
  };

  // ---------- 批次操作 ----------
  const handleBatchStatus = async (id: string, status: string) => {
    if (status === "revoked" && !window.confirm("作废整批后，这批里所有尚未兑换的码都会失效。确认？")) return;
    try {
      await callAdmin("set_batch_status", { batch_id: id, status });
      loadBatches();
    } catch (e) {
      fail(e);
    }
  };

  // ---------- 查码 ----------
  const [lookupInput, setLookupInput] = useState("");
  const [lookup, setLookup] = useState<any>(null);
  const [backerRef, setBackerRef] = useState("");
  const [reissued, setReissued] = useState<{ code: string; redeem_url: string } | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    setReissued(null);
    try {
      const r = await callAdmin("lookup_code", { code: lookupInput });
      setLookup(r);
      setBackerRef(r.code?.backer_ref ?? "");
      setError("");
    } catch (err) {
      setLookup(null);
      fail(err);
    }
  };

  const handleSetBacker = async () => {
    try {
      await callAdmin("set_backer_ref", { code: lookupInput, backer_ref: backerRef });
      flash("已保存 CAMPFIRE 支援编号");
    } catch (e) {
      fail(e);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("确认作废这个码？")) return;
    try {
      await callAdmin("revoke_code", { code: lookupInput });
      flash("已作废");
      loadBatches();
    } catch (e) {
      fail(e);
    }
  };

  const handleReissue = async () => {
    if (!window.confirm("作废旧码并在原批次下生成一个新码（截止日不变）。确认？")) return;
    try {
      const r = await callAdmin<{ code: string; redeem_url: string }>("reissue_code", { code: lookupInput });
      setReissued(r);
      flash("已补发。新码只显示这一次，请立刻复制。");
      loadBatches();
    } catch (e) {
      fail(e);
    }
  };

  // ---------- 直接发放 ----------
  const [grantUser, setGrantUser] = useState("");
  const [grantDays, setGrantDays] = useState(30);
  const [grantReason, setGrantReason] = useState("");
  const [grantBadge, setGrantBadge] = useState("");

  const handleGrant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!window.confirm(`确认给会员 ${grantUser} 直接增加 ${grantDays} 天 VIP？`)) return;
    try {
      const r = await callAdmin<{ wallet_days_after: number }>("grant_direct", {
        user_id: grantUser.trim(),
        days: grantDays,
        reason: grantReason,
        badge: grantBadge || null,
      });
      flash(`发放成功，该会员当前剩余 ${r.wallet_days_after} 天`);
      setGrantReason("");
    } catch (err) {
      fail(err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">众筹兑换码</h1>
        <Link to="/admin/dashboard" className="text-sm text-blue-600 hover:text-blue-500">
          &larr; 返回控制面板
        </Link>
      </div>

      {error && <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">{error}</div>}
      {info && <div className="p-3 bg-green-100 text-green-800 rounded-md text-sm">{info}</div>}

      {/* 建批次 */}
      <section className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-1">1. 生成一批兑换码</h2>
        <p className="text-sm text-gray-500 mb-4">
          兑换截止日不能晚于「今天起 180 天」（数据库强制）。F 档每位支持者发 2 个码，数量请填 支持者人数 × 2。
        </p>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">档位预设</span>
            <select className={inputCls} value={presetIdx} onChange={(e) => applyPreset(Number(e.target.value))}>
              {PRESETS.map((p, i) => (
                <option key={p.tier} value={i}>{p.label}</option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">活动名（campaign）</span>
            <input className={inputCls} value={campaign} onChange={(e) => setCampaign(e.target.value)} required />
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">批次名称</span>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">VIP 天数（1~1095）</span>
            <input type="number" className={inputCls} min={1} max={1095} value={days} onChange={(e) => setDays(Number(e.target.value))} required />
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">徽章</span>
            <select className={inputCls} value={badge} onChange={(e) => setBadge(e.target.value)}>
              <option value="">无</option>
              <option value="supporter">supporter</option>
              <option value="founder">founder</option>
              <option value="patron">patron</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">数量（1~2000）</span>
            <input type="number" className={inputCls} min={1} max={2000} value={count} onChange={(e) => setCount(Number(e.target.value))} required />
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">兑换截止日（含当天）</span>
            <input type="date" className={inputCls} value={deadline} min={dateInputValue(0)} max={dateInputValue(179)} onChange={(e) => setDeadline(e.target.value)} required />
          </label>
          <label className="text-sm md:col-span-2">
            <span className="block mb-1 text-gray-600">备注</span>
            <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <div className="md:col-span-3">
            <button type="submit" disabled={loading} className={btnCls}>
              {loading ? "生成中…" : "生成兑换码"}
            </button>
          </div>
        </form>

        {created && (
          <div className="mt-6 border border-amber-300 bg-amber-50 rounded-md p-4">
            <p className="text-sm text-amber-900 mb-3">
              ⚠️ 已生成 {created.codes.length} 个码（截止 {new Date(created.meta.redeem_deadline).toLocaleDateString()}）。
              明文码只显示这一次，请立刻下载 CSV 并妥善保管。
            </p>
            <div className="flex gap-3 mb-3">
              <button onClick={handleDownload} className={btnCls}>下载 CSV</button>
              <button onClick={() => setCreated(null)} className={btnGhost}>我已保存，关闭</button>
            </div>
            <div className="max-h-40 overflow-auto font-mono text-xs text-gray-700 bg-white border rounded p-2">
              {created.codes.slice(0, 20).map((c) => (
                <div key={c.code}>{c.code}</div>
              ))}
              {created.codes.length > 20 && <div>… 其余 {created.codes.length - 20} 个见 CSV</div>}
            </div>
          </div>
        )}
      </section>

      {/* 批次列表 */}
      <section className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-4">2. 批次与兑换情况</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-4">名称</th>
                <th className="py-2 pr-4">档</th>
                <th className="py-2 pr-4">天数</th>
                <th className="py-2 pr-4">已兑换 / 总数</th>
                <th className="py-2 pr-4">未兑换</th>
                <th className="py-2 pr-4">截止</th>
                <th className="py-2 pr-4">状态</th>
                <th className="py-2">操作</th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id} className="border-b last:border-0">
                  <td className="py-2 pr-4">{b.name}<div className="text-xs text-gray-400">{b.campaign}</div></td>
                  <td className="py-2 pr-4">{b.reward_tier}</td>
                  <td className="py-2 pr-4">{b.vip_days}</td>
                  <td className="py-2 pr-4">{b.redeemed} / {b.code_count}</td>
                  <td className="py-2 pr-4">{b.unused}</td>
                  <td className="py-2 pr-4">
                    {new Date(b.redeem_deadline).toLocaleDateString()}
                    {b.expired && <span className="ml-1 text-red-600">已过期</span>}
                  </td>
                  <td className="py-2 pr-4">{b.reward_tier === "admin" ? "客服直接发放" : b.status}</td>
                  <td className="py-2 space-x-2 whitespace-nowrap">
                    {b.reward_tier === "admin" ? null : <>
                    {b.status === "active" && <button className={btnGhost} onClick={() => handleBatchStatus(b.id, "paused")}>暂停</button>}
                    {b.status === "paused" && <button className={btnGhost} onClick={() => handleBatchStatus(b.id, "active")}>恢复</button>}
                    {b.status !== "revoked" && <button className={btnGhost} onClick={() => handleBatchStatus(b.id, "revoked")}>作废整批</button>}
                    </>}
                  </td>
                </tr>
              ))}
              {batches.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-gray-400">还没有批次</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* 查码 */}
      <section className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-1">3. 客服：查码 / 记录支援编号 / 作废 / 补发</h2>
        <p className="text-sm text-gray-500 mb-4">输入完整码，或该码末 4 位（末 4 位重复时请用完整码）。</p>
        <form onSubmit={handleLookup} className="flex gap-3 mb-4">
          <input className={inputCls} value={lookupInput} onChange={(e) => setLookupInput(e.target.value)} placeholder="XXXX-XXXX-XXXX-XXXX 或 末4位" required />
          <button type="submit" className={btnCls}>查询</button>
        </form>
        {lookup && !lookup.found && <p className="text-sm text-gray-500">没有找到这个码。</p>}
        {lookup?.found && (
          <div className="text-sm space-y-2">
            <div>状态：<b>{lookup.code.status}</b>　批次：{lookup.batch?.name}（{lookup.batch?.vip_days} 天，截止 {lookup.batch && new Date(lookup.batch.redeem_deadline).toLocaleDateString()}，批次状态 {lookup.batch?.status}）</div>
            {lookup.code.status === "redeemed" && (
              <div>
                兑换时间：{new Date(lookup.code.redeemed_at).toLocaleString()}　兑换会员：
                {lookup.redeemer ? `${lookup.redeemer.display_name ?? "(无昵称)"}（${lookup.redeemer.id}）` : "（账号已删除）"}
              </div>
            )}
            <div className="flex gap-2 items-center">
              <span>CAMPFIRE 支援编号：</span>
              <input className={`${inputCls} max-w-xs`} value={backerRef} onChange={(e) => setBackerRef(e.target.value)} />
              <button className={btnGhost} onClick={handleSetBacker}>保存</button>
            </div>
            {lookup.code.status !== "redeemed" && (
              <div className="space-x-2 pt-2">
                {lookup.code.status === "unused" && <button className={btnGhost} onClick={handleRevoke}>作废</button>}
                <button className={btnGhost} onClick={handleReissue}>作废并补发新码</button>
              </div>
            )}
            {reissued && (
              <div className="mt-2 p-3 border border-amber-300 bg-amber-50 rounded font-mono text-xs break-all">
                新码：{reissued.code}<br />{redeemUrlFor(reissued.code)}
              </div>
            )}
          </div>
        )}
      </section>

      {/* 直接发放 */}
      <section className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold mb-1">4. 客服：直接发放 VIP 天数</h2>
        <p className="text-sm text-gray-500 mb-4">
          用于码彻底丢失、或支持者在備考欄填了会员 ID 的情况。每次发放都会留下一条「admin」批次记录，请务必写明理由。
          会员 ID 在 App「设置」页可复制。
        </p>
        <form onSubmit={handleGrant} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <label className="text-sm md:col-span-2">
            <span className="block mb-1 text-gray-600">会员 ID（UUID）</span>
            <input className={inputCls} value={grantUser} onChange={(e) => setGrantUser(e.target.value)} required />
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">天数</span>
            <input type="number" className={inputCls} min={1} max={1095} value={grantDays} onChange={(e) => setGrantDays(Number(e.target.value))} required />
          </label>
          <label className="text-sm">
            <span className="block mb-1 text-gray-600">徽章</span>
            <select className={inputCls} value={grantBadge} onChange={(e) => setGrantBadge(e.target.value)}>
              <option value="">不变</option>
              <option value="supporter">supporter</option>
              <option value="founder">founder</option>
              <option value="patron">patron</option>
            </select>
          </label>
          <label className="text-sm md:col-span-4">
            <span className="block mb-1 text-gray-600">理由（必填）</span>
            <input className={inputCls} value={grantReason} onChange={(e) => setGrantReason(e.target.value)} required />
          </label>
          <div className="md:col-span-4">
            <button type="submit" className={btnCls}>发放</button>
          </div>
        </form>
      </section>
    </div>
  );
}
